// HTTP 客户端基础模块
import { APIResponse, APIError } from '@/types/api';

// HTTP 客户端配置
interface HttpClientConfig {
  baseURL: string;
  timeout: number;
  retries: number;
  retryDelay: number;
}

// 请求选项扩展
interface RequestOptions extends RequestInit {
  params?: Record<string, any>;
  timeout?: number; // 自定义超时时间（毫秒）
}

// 请求拦截器类型
export type RequestInterceptor = (config: RequestInit) => RequestInit | Promise<RequestInit>;
// 响应拦截器类型
export type ResponseInterceptor = (response: Response) => Response | Promise<Response>;
// 错误拦截器类型
export type ErrorInterceptor = (error: APIError) => APIError | Promise<APIError>;

class HttpClient {
  private config: HttpClientConfig;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private errorInterceptors: ErrorInterceptor[] = [];

  constructor(config: Partial<HttpClientConfig> = {}) {
    this.config = {
      baseURL: '/api/v1',
      timeout: 60000, // 60秒超时，适配大文件操作
      retries: 3,
      retryDelay: 1000,
      ...config,
    };
  }

  // 添加请求拦截器
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
  }

  // 添加响应拦截器
  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
  }

  // 添加错误拦截器
  addErrorInterceptor(interceptor: ErrorInterceptor): void {
    this.errorInterceptors.push(interceptor);
  }

  // 获取认证头
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  // 构建完整URL
  private buildURL(endpoint: string): string {
    const baseURL = this.config.baseURL.endsWith('/') 
      ? this.config.baseURL.slice(0, -1) 
      : this.config.baseURL;
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${baseURL}${cleanEndpoint}`;
  }

  // 构建查询参数
  private buildQueryString(params: Record<string, any>): string {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(item => searchParams.append(key, String(item)));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  // 处理请求拦截器
  private async processRequestInterceptors(config: RequestInit): Promise<RequestInit> {
    let processedConfig = config;
    
    for (const interceptor of this.requestInterceptors) {
      processedConfig = await interceptor(processedConfig);
    }
    
    return processedConfig;
  }

  // 处理响应拦截器
  private async processResponseInterceptors(response: Response): Promise<Response> {
    let processedResponse = response;
    
    for (const interceptor of this.responseInterceptors) {
      processedResponse = await interceptor(processedResponse);
    }
    
    return processedResponse;
  }

  // 处理错误拦截器
  private async processErrorInterceptors(error: APIError): Promise<APIError> {
    let processedError = error;
    
    for (const interceptor of this.errorInterceptors) {
      processedError = await interceptor(processedError);
    }
    
    return processedError;
  }

  // 重试机制
  private async retryRequest<T>(
    requestFn: () => Promise<T>,
    retries: number = this.config.retries
  ): Promise<T> {
    try {
      return await requestFn();
    } catch (error: any) {
      if (retries > 0 && this.shouldRetry(error)) {
        await this.delay(this.config.retryDelay);
        return this.retryRequest(requestFn, retries - 1);
      }
      throw error;
    }
  }

  // 判断是否应该重试
  private shouldRetry(error: any): boolean {
    // 获取错误状态码（可能是 status 或 code）
    const statusCode = error.status || error.code;
    
    // 4xx 客户端错误（如 404, 400, 401, 403）不应该重试
    // 只有网络错误或5xx服务器错误时才重试
    if (statusCode) {
      // 4xx 错误不重试
      if (statusCode >= 400 && statusCode < 500) {
        return false;
      }
      // 5xx 错误重试
      return statusCode >= 500;
    }
    
    // 没有状态码的错误（如网络错误）应该重试
    return true;
  }

  // 延迟函数
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // 解析API响应
  private async parseResponse<T>(response: Response): Promise<APIResponse<T>> {
    // 对于错误响应，需要安全地解析响应体
    let data: any = {};
    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');
    
    // 先读取响应体（只能读取一次）
    let responseText = '';
    try {
      responseText = await response.text();
    } catch (e) {
      // 读取失败，忽略
    }
    
    // 尝试解析 JSON
    if (responseText && isJson) {
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        // JSON 解析失败，保持空对象
        data = {};
      }
    }
    
    // 检查HTTP状态码
    // 404 错误不抛出异常（用于处理资源不存在的情况，如策略不存在）
    if (!response.ok) {
      const error: APIError = {
        code: response.status,
        message: data.message || data.error || `HTTP error! status: ${response.status}`,
        details: data,
      };
      throw error;
    }

    // 检查响应体中的业务错误码
    // 404 错误不抛出异常（用于处理资源不存在的情况，如策略不存在）
    if (data.code && data.code !== 200 && data.code !== 404) {
      const error: APIError = {
        code: data.code,
        message: data.message || `业务错误! code: ${data.code}`,
        details: data,
      };
      throw error;
    }

    return data;
  }

  // 通用请求方法
  async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<APIResponse<T>> {
    const { params, timeout, ...requestOptions } = options;
    
    // 使用自定义超时时间或默认超时时间
    const requestTimeout = timeout || this.config.timeout;
    
    // 构建URL
    let url = this.buildURL(endpoint);
    if (params) {
      url += this.buildQueryString(params);
    }

    // 构建请求配置
    // 如果 requestOptions 中已经有 headers，合并它们；否则使用默认的认证头
    const defaultHeaders = this.getAuthHeaders();
    const config: RequestInit = {
      method: 'GET',
      headers: requestOptions.headers 
        ? { ...defaultHeaders, ...requestOptions.headers }
        : defaultHeaders,
      ...requestOptions,
    };
    
    // 如果是 FormData，删除 Content-Type 让浏览器自动设置
    if (requestOptions.body instanceof FormData && config.headers) {
      const headers = new Headers(config.headers);
      headers.delete('Content-Type');
      config.headers = headers;
    }

    // 处理请求拦截器
    const processedConfig = await this.processRequestInterceptors(config);

    // 执行请求（带重试机制）
    const requestFn = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

      try {
        const response = await fetch(url, {
          ...processedConfig,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // 处理响应拦截器
        const processedResponse = await this.processResponseInterceptors(response);

        // 解析响应
        return await this.parseResponse<T>(processedResponse);
      } catch (error: any) {
        clearTimeout(timeoutId);
        
        if (error.name === 'AbortError') {
          throw {
            code: 408,
            message: '请求超时',
            details: error,
          } as APIError;
        }
        
        // 如果已经是 APIError，直接抛出
        if (error.code && typeof error.code === 'number') {
          throw error;
        }
        
        // 其他错误（如网络错误、JSON解析错误等），转换为 APIError
        throw {
          code: 500,
          message: error.message || '网络请求失败',
          details: error,
        } as APIError;
      }
    };

    try {
      return await this.retryRequest(requestFn);
    } catch (error: any) {
      // 处理错误拦截器
      const processedError = await this.processErrorInterceptors(error);
      throw processedError;
    }
  }

  // GET 请求
  async get<T>(endpoint: string, params?: Record<string, any>, timeout?: number): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', params, timeout });
  }

  // POST 请求
  async post<T>(endpoint: string, data?: any, params?: Record<string, any>, options?: RequestOptions): Promise<APIResponse<T>> {
    // 如果是 FormData，直接使用，不进行 JSON.stringify
    const body = data instanceof FormData 
      ? data 
      : (data ? JSON.stringify(data) : undefined);
    
    return this.request<T>(endpoint, {
      method: 'POST',
      body,
      params,
      ...options,
    });
  }

  // PUT 请求
  async put<T>(endpoint: string, data?: any, params?: Record<string, any>, timeout?: number): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      params,
      timeout,
    });
  }

  // DELETE 请求
  async delete<T>(endpoint: string, params?: Record<string, any>, timeout?: number): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE', params, timeout });
  }

  // PATCH 请求
  async patch<T>(endpoint: string, data?: any, params?: Record<string, any>, timeout?: number): Promise<APIResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      params,
      timeout,
    });
  }
}

// 创建默认HTTP客户端实例
export const httpClient = new HttpClient();

// 导出类型和类
export { HttpClient };
export type { HttpClientConfig };
